import { useEffect, useState } from 'react';
import * as React from 'react';
import Checkbox from '../../Shared/Primitive/Checkbox';
import { useDispatch, useSelector } from 'react-redux';
import { setModalData } from '../../Shared/SharedSlices/ModalSlice';
import Button from '../../Shared/Primitive/Button';
import { selectTranslations } from '../../../App/i18n/i18nSlice';
import { RootState } from '../../../App/store';
import HTTPService from '../../../App/HTTPService';
import { headers } from '../../../App/Axios';
import { MediaLibraryStats } from '../../../Types/MediaLibrary';
import { MediaLibraryStatsGetRequest } from '../RoomServiceRequests/MediaLibraryRequests';
import FileBrowser from '../MediaLibrary/FileBrowser';
import FileItemPreview from '../MediaLibrary/FileItemPreview';
import SearchInput from '../../Shared/Primitive/SearchInput';
import { setMediaLibraryData } from '../../Shared/SharedSlices/MediaLibrarySlice';
import { Upload } from '../../Shared/Components/SvgIcons';
import SessionService from '../../../App/SessionService';
import { RoleType, RoomType, StorageKey } from '../../../App/enums';
import TutorialBubble from '../../Shared/Components/TutorialBubble';

const MediaLibrary: React.FC = () => {
    const mediaLibraryState = useSelector((state: RootState) => state.MediaLibrarySlice)
    const roomDataState = useSelector((state: RootState) => state.roomSlice.roomSlice.room)
    const organizationDataState = useSelector((state: RootState) => state.organizationsSlice.organizationSlice.organization)
    const [mediaLibraryStats, setMediaLibraryStats] = useState<MediaLibraryStats>()
    const isMemberInRoom = useSelector((state: RootState) => state.MemberInRoomStatusSlice.MemberInRoomStatusSlice.isMember)
    const dispatch = useDispatch()
    const [isOrgAdmin, setIsOrgAdmin] = React.useState(false)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const orgId = SessionService.getItem(StorageKey.organizationId)
    const translation = useSelector(selectTranslations)

    useEffect(() => {
        if (roomDataState.id === undefined) { return }

        const requestParams: [number, number?] = [roomDataState.id];
        if (role === RoleType.ORGANIZATION && orgId !== undefined) {
            requestParams.push(Number(orgId!));
        }

        MediaLibraryStatsGetRequest(...requestParams).then(response => {
            if (response.status === 200) {
                setMediaLibraryStats(response.data)
            }
        })
    }, [mediaLibraryState.items])

    useEffect(() => {
        if (Number(roomDataState.organization?.id)) {
            HTTPService.get(`/role-in-org/${roomDataState.organization?.id}/`, headers).then(response => {
                if (response.status === 200) {
                    setIsOrgAdmin(response.data.is_admin)
                }
            })
        }
    }, [roomDataState.organization?.id])

    const onUploadButtonClick = () => {
        if (roomDataState?.room_permissions[0]?.only_admins_can_upload && !isOrgAdmin) {
            dispatch(setModalData({ open: true, onlyAdminsCanUploadToLibrary: true }))
        } else {
            dispatch(setModalData({ open: true, roomUploadModal: true }))
        }
    }

    if (roomDataState.type === RoomType.Private && (isMemberInRoom === "pending" || isMemberInRoom === "non-member")) {
            return <div className="room-layout__right-panel-content">
                 <div className="room-layout__no-access">{translation.cantSeeRoomContent}</div>
            </div>
        }

    return (
        <div className="media-library__container">
            <TutorialBubble text={translation.tutorial_org_media_library} targetSelector='[data-tutorial-anchor="room-media-library"]' placement="bottom" visible={roomDataState.organization?.id === organizationDataState.id && organizationDataState.tutorial_progress === 5} hasNext={true} routeOnNext={`/room/${organizationDataState.id}/${roomDataState.id}?tab=events`}/>

            <div className='media-library-header' data-tutorial-anchor="room-media-library">
                <div className='media-library-header__actions'>
                    <div className='media-library-header__upload-button-mobile' onClick={onUploadButtonClick}>
                        <Upload />
                    </div>
                    <div className='media-library-header__upload-button-desktop' onClick={onUploadButtonClick}>
                        <Button
                            className="button button--border-radius-05 button--color-gold button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-small button--font-weight-400"
                        >
                            {translation.uploadFile}
                        </Button>
                    </div>
                    <div className='media-library-header__search-input'>
                        <SearchInput
                            onRemoveIconClick={() => dispatch(setMediaLibraryData({ ...mediaLibraryState, filter: { ...mediaLibraryState.filter, query: '' } }))}
                            searchvalue={mediaLibraryState.filter.query}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                dispatch(setMediaLibraryData({ ...mediaLibraryState, filter: { ...mediaLibraryState.filter, query: event.target.value } }))
                            }}
                            isGlobalSearch={false}
                            isChatSearch={false}
                        />
                    </div>
                    <div className='media-library-header__checkboxes'>
                        <div className="media-library-header__checkbox-container">
                            <Checkbox color="dark-blue"
                                checked={mediaLibraryState.filter.documents}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => dispatch(setMediaLibraryData({ ...mediaLibraryState, filter: { ...mediaLibraryState.filter, documents: event.target.checked } }))}
                            />
                            <div className="media-library-header__text">
                                Dokumente
                            </div>
                        </div>

                        <div className="media-library-header__checkbox-container">
                            <Checkbox color="dark-blue"
                                checked={mediaLibraryState.filter.images}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => dispatch(setMediaLibraryData({ ...mediaLibraryState, filter: { ...mediaLibraryState.filter, images: event.target.checked } }))}
                            />
                            <div className="media-library-header__text">
                                Bilder
                            </div>
                        </div>

                    </div>
                    <div className='media-library-header__file-counter'>
                        {mediaLibraryStats?.total_items} {mediaLibraryStats?.total_items === 1 ? 'Datei' : 'Dateien'} | {mediaLibraryStats?.total_size ? (mediaLibraryStats.total_size / (1024 * 1024) > 1024 ? (mediaLibraryStats.total_size / (1024 * 1024 * 1024)).toFixed(2) + ' GB' : (mediaLibraryStats.total_size / (1024 * 1024)).toFixed(2) + ' MB') : '0 MB'} | {mediaLibraryStats?.total_size ? ((mediaLibraryStats.total_size / (5 * 1024 * 1024 * 1024)) * 100).toFixed(2) + '%' : '0%'} belegter Speicher   
                    </div>

                </div>
            </div>
            <div className='media-library__content'>
                <FileBrowser isOrgAdmin={isOrgAdmin} />
                {mediaLibraryState.selectedItem && mediaLibraryState.selectedItem.file_name && <FileItemPreview isOrgAdmin={isOrgAdmin} />}
            </div>
        </div>
    );
};

export default MediaLibrary;